import React, { useCallback, useState } from 'react';
import "./RecipientFilterReviewModal.scss";
import { Button, Modal, Spinner, TextField, Alert } from '@livingdesign/react';
import { formatFilterToRender, mapAttributeToLabel } from '../../../../../../../utils/recipientUtils';
import { ACTIONS } from '../../../../../../../consts/CommonV2';

export const QA_ID_PREFIX = "RecipientFilterReviewModal";

function RecipientFilterReviewModal({
    isOpen,
    onClose,
    parsedFilter = [],
    recipientsCount = 0,
    handleApplyFilters,
    filterName,
    setFilterName,
    filterId,
    saveNewFilter,
    updateFilter,
    mode=ACTIONS.CAMPAIGN_PAGE,
    onSuccessUpdate
}) {

    const [saveFilter, setSaveFilter] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Helper function to handle filter save/update operations
    const handleFilterOperation = useCallback(async () => {
        if (filterId) {
            const result = await updateFilter();
            if (result) {
                throw new Error(result?.response?.data?.message);
            }
        } else {
            const result = await saveNewFilter();
            if (result) {
                throw new Error(result?.response?.data?.message);
            }
        }
    }, [filterId, updateFilter, saveNewFilter]);

    // Main handler for the confirm/apply button
    const handleConfirmClick = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Handle filter save/update operations
            if (saveFilter || mode != ACTIONS.CAMPAIGN_PAGE) {
                await handleFilterOperation();
            }
            
            // Handle post-operation actions
            if (mode === ACTIONS.CAMPAIGN_PAGE) {
                await handleApplyFilters();
            } else {
                // Only call onSuccessUpdate if no errors occurred
                onSuccessUpdate();
            }
        } catch (err) {
            console.error('Error in filter operation:', err);
            setError(err?.message ||  "Please check your input and try again.");
        } finally {
            setLoading(false);
        }
    }, [saveFilter, mode, handleFilterOperation, handleApplyFilters, onSuccessUpdate]);

    const renderFilters = useCallback(() => {
        const filters = formatFilterToRender(parsedFilter);
        return (
            filters.map((filter, index) => (
                <div
                  key={index}
                  className='filter-row'
                  style={{ margin: "16px 0" }}
                  data-testid={`${QA_ID_PREFIX}-filter-row-${index}`}
                >
                    <span
                      className='filter-attribute-column'
                      data-testid={`${QA_ID_PREFIX}-filter-attribute-${index}`}
                    >
                      {mapAttributeToLabel(filter["attribute"])}:&nbsp;
                    </span>
                    <span
                      className='filter-values-column'
                      data-testid={`${QA_ID_PREFIX}-filter-values-${index}`}
                    >
                        {filter["values"]?.map((value, valIndex) => (
                            <span
                              key={valIndex}
                              className='filter-value-item'
                              data-testid={`${QA_ID_PREFIX}-filter-value-${index}-${valIndex}`}
                            >
                              {value}
                            </span>
                        ))}
                    </span>
                    &nbsp;
                    {filter["operator"] && (
                      <span
                        className='filter-operator-column'
                        data-testid={`${QA_ID_PREFIX}-filter-operator-${index}`}
                      >
                        {filter["operator"]}
                      </span>
                    )}
                </div>
            ))
        );
    }, [parsedFilter]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={ mode === ACTIONS.RECIPIENT_PAGE_FILTER_EDIT ? "Review edited recipient filters" : "Review recipient filters" }
            size="medium"
            actions={(
                <div style={{display: 'flex', alignItems: 'center'}} data-testid={`${QA_ID_PREFIX}-actions`}>
                    <Button
                        disabled={loading}
                        onClick={onClose}
                        variant='secondary'
                        UNSAFE_style={{ marginRight: 8 }}
                        data-testid={`${QA_ID_PREFIX}-cancel-button`}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={(saveFilter && !filterName.trim()) || loading}
                        onClick={handleConfirmClick}
                        variant='primary'
                        data-testid={`${QA_ID_PREFIX}-confirm-button`}
                    >
                        {loading && <Spinner size='small' />}
                       { mode === ACTIONS.CAMPAIGN_PAGE ? 
                       ( "Apply filters" ): 
                       ( mode === ACTIONS.RECIPIENT_PAGE_FILTER_CREATE ? "Save Filters" : "Confirm Changes" )}
                    </Button>
                </div>
            )}
            data-testid={`${QA_ID_PREFIX}-modal`}
        >
            <div className='recipient-filter-review-modal' data-testid={`${QA_ID_PREFIX}-container`}>
                <p data-testid={`${QA_ID_PREFIX}-description`}>Review the filtered results to ensure they reflect your selected criteria.</p>
                
                {error && (
                    <Alert 
                        variant="error" 
                        style={{ marginTop: "16px", marginBottom: "16px" }}
                        onClose={() => setError(null)}
                        data-testid={`${QA_ID_PREFIX}-error-alert`}
                    >
                        {error}
                    </Alert>
                )}
                
                { mode !== ACTIONS.CAMPAIGN_PAGE && (
                    <div style={{ marginTop: "16px" }} data-testid={`${QA_ID_PREFIX}-name-input-section-recipient`}>
                        <div className='secondary-typo' style={{ marginBottom: "8px" }}>*Required field</div>
                        <TextField
                            label="Recipient list name*"
                            value={filterName}
                            onChange={e => setFilterName(e.target.value)}
                            data-testid={`${QA_ID_PREFIX}-name-input-recipient`}
                        />
                        <div className='secondary-typo' style={{ marginTop: "8px" }}>Choose a name for your recipient list to easily find and use it later.</div>
                    </div>
                )}
                <div data-testid={`${QA_ID_PREFIX}-to-section`}>
                    <h3>To:</h3>
                    <div className='recipients-count' data-testid={`${QA_ID_PREFIX}-recipients-count`}>
                        {recipientsCount} Recipients
                    </div>
                </div>
                <div data-testid={`${QA_ID_PREFIX}-applied-filters-section`}>
                    <h3>Applied filters:</h3>
                    <div className='applied-filters' data-testid={`${QA_ID_PREFIX}-applied-filters`}>
                        {renderFilters()}
                    </div>
                </div>
                { 
                    mode===ACTIONS.CAMPAIGN_PAGE ?
                    (

                        <div className='save-filters-note' data-testid={`${QA_ID_PREFIX}-save-filters-note`}>
                            <input
                                type='checkbox'
                                style={{ margin: "3px 3px 3px 0" }}
                                checked={saveFilter}
                                onChange={() => setSaveFilter(!saveFilter)}
                                data-testid={`${QA_ID_PREFIX}-save-filters-checkbox`}
                            /> Save these recipient filters for future
                        </div>
                    ) : null
                }
                {saveFilter && mode===ACTIONS.CAMPAIGN_PAGE && (
                    <div style={{ marginTop: "16px" }} data-testid={`${QA_ID_PREFIX}-name-input-section-campaign`}>
                        <div className='secondary-typo' style={{ marginBottom: "8px" }}>*Required field</div>
                        <TextField
                            label="Recipient list name*"
                            value={filterName}
                            onChange={e => setFilterName(e.target.value)}
                            data-testid={`${QA_ID_PREFIX}-name-input-campaign`}
                        />
                        <div className='secondary-typo' style={{ marginTop: "8px" }}>Choose a name for your recipient list to easily find and use it later.</div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default RecipientFilterReviewModal;